import axios from "axios";
import {
  JACKPOT_VALUE_TO_ID,
  BLANK_SYMBOL_ID,
  ZEUS_DOUBLE_ID,
  ZEUS_MYSTERY_ID,
  ZEUS_COLLECT_ID,
  CD_MULT_ID,
  CM_MINOR_ID,
  CM_MINI_ID,
  CM_MAJOR_ID,
} from "../../../SymbolData";
import { FEATURES_CONFIG } from "../../../featuresConfig";
import StorageManager from "../../../utils/StorageManager";
import { PROVIDER_CONFIG } from "./providerConfig";

// Parse bonus screen symbol: "0" → 0, "coin_10" → 20, "coin_25" → 27 (jackpot)
function parseBonusSymbol(symbolId) {
  if (typeof symbolId === "number") return symbolId;
  if (typeof symbolId !== "string") return 0;

  if (symbolId.startsWith("coin_")) {
    const coinValue = parseInt(symbolId.slice(5), 10);
    if (isNaN(coinValue)) return 0;
    if (JACKPOT_VALUE_TO_ID[coinValue] !== undefined) {
      return JACKPOT_VALUE_TO_ID[coinValue];
    }
    return 10 + coinValue;
  }

  const n = parseInt(symbolId, 10);
  return isNaN(n) ? 0 : n;
}

// Convert API screen (string IDs) → numeric symbol IDs through SYMBOL_MAP
function normalizeScreenData(screenData) {
  if (!screenData || !Array.isArray(screenData)) return screenData;
  const map = PROVIDER_CONFIG.SYMBOL_MAP;
  return screenData.map((reel) => {
    if (!Array.isArray(reel)) return reel;
    return reel.map((symbolId) => {
      const n = parseBonusSymbol(symbolId);
      return map[n] !== undefined ? map[n] : n;
    });
  });
}

// Web Worker factory
function createInlineWorker(code) {
  const blob = new Blob([code], { type: "application/javascript" });
  const url = URL.createObjectURL(blob);
  return new Worker(url);
}

// Inline spin worker — runs API calls off the main thread
const spinWorkerCode = `
  importScripts("https://cdn.jsdelivr.net/npm/axios/dist/axios.min.js");

  onmessage = async (event) => {
    const { bet, token, apiUrl, csrfTokenName, csrfTokenValue, identifier, command = "spin", purchasedFeature, purchasedFeatureLevel, options } = event.data;

    if (!bet || !token || !apiUrl || !csrfTokenName || !csrfTokenValue) {
      postMessage({ error: "Dmon: Spin API call failed: Missing required parameters" });
      return;
    }

    try {
      // Build request body
      let payloadOptions = { bet: bet, ...(options || {}) };
      if (purchasedFeature) {
        payloadOptions.purchased_feature = purchasedFeature;
      }
      if (purchasedFeatureLevel) {
        payloadOptions.purchased_feature_level = String(purchasedFeatureLevel);
      }
      const requestBody = { command: command, options: payloadOptions };

      const response = await axios.post(
        apiUrl,
        requestBody,
        {
          headers: {
            [csrfTokenName]: csrfTokenValue,
            "Content-Type": "application/json"
          }
        }
      );

      if (!response.data) {
        postMessage({ error: "Dmon: Spin API call failed: Invalid response structure" });
        return;
      }

      if (response.data.errors && Array.isArray(response.data.errors) && response.data.errors.length > 0) {
        postMessage({ error: response.data });
        return;
      }

      postMessage(response.data);

    } catch (error) {
      if (error.response?.data?.errors) {
        postMessage({ error: error.response.data });
      } else {
        postMessage({ error: "Dmon: Spin API call failed: " + error.message });
      }
    }
  };
`;

// ─────────────────────────────────────────────
// Dmon RGS — 3 Orbs of Zeus
// ─────────────────────────────────────────────

class DmonRGS {
  constructor() {
    this.api_base_url = window.__OPTIONS__?.api || "";
    this.worker = null;
    this.currentToken = null;
    this.lines = null;
  }

  // ── Win Classification ──

  computeWinType(winAmount, betAmount) {
    if (!winAmount || winAmount <= 0 || !betAmount || betAmount <= 0) return "";

    const multiplier = winAmount / betAmount;
    const thresholds = PROVIDER_CONFIG.WIN_THRESHOLDS;
    const winTypes = PROVIDER_CONFIG.WIN_TYPES;

    const sorted = Object.entries(thresholds).sort(([, a], [, b]) => b - a);
    for (const [thresholdKey, thresholdValue] of sorted) {
      if (multiplier >= thresholdValue) {
        const winTypeKey = thresholdKey.replace("_THRESHOLD", "");
        return winTypes[winTypeKey] || "regular_win";
      }
    }
    return "regular_win";
  }

  // ── Win Normalization ──

  // Match row positions against init line definitions to find line ID
  resolveLineId(positions) {
    if (!this.lines || !Array.isArray(positions)) return null;
    for (let i = 0; i < this.lines.length; i++) {
      const line = this.lines[i];
      if (!Array.isArray(line) || line.length !== positions.length) continue;
      let match = true;
      for (let j = 0; j < positions.length; j++) {
        if (line[j] !== positions[j]) {
          match = false;
          break;
        }
      }
      if (match) return i;
    }
    return null;
  }

  // Convert raw API wins to state format
  // API formats:
  //   Line win:  ["line", amount, [row0, row1, ...], lineId]
  //   Coin win:  ["coin", value, reel, row]
  //   Bonus:     ["coins_game", totalWin]
  //   Jackpot:   ["jackpot", value]
  normalizeWins(rawWins, screen) {
    if (!Array.isArray(rawWins)) return [];
    return rawWins
      .map((winTuple) => {
        if (!Array.isArray(winTuple)) return null;

        // Coin win: individual coin payout
        if (winTuple[0] === "coin") {
          const [, value, reel, row] = winTuple;
          const rawSymbol = screen?.[reel]?.[row];
          let mappedSymbol = null;
          if (rawSymbol !== undefined) {
            mappedSymbol = PROVIDER_CONFIG.SYMBOL_MAP[rawSymbol] !== undefined
              ? PROVIDER_CONFIG.SYMBOL_MAP[rawSymbol]
              : (typeof rawSymbol === "string" ? parseInt(rawSymbol, 10) : rawSymbol);
          }
          return {
            id: null,
            win: value || 0,
            positions: [[reel, row]],
            symbol: mappedSymbol,
            isCoin: true,
          };
        }

        // Bonus completion: total coins_game win
        if (winTuple[0] === "coins_game") {
          return {
            id: null,
            win: winTuple[1] || 0,
            positions: [],
            symbol: null,
            isBonus: true,
          };
        }

        // Grand jackpot
        if (winTuple[0] === "jackpot") {
          return {
            id: null,
            win: winTuple[1] || 0,
            positions: [],
            symbol: null,
            isJackpot: true,
            jackpotType: "grand",
          };
        }

        // Line win: ["line", amount, [rowPositions], lineId]
        if (winTuple.length < 4) return null;
        const [, amount, positions, lineId] = winTuple;
        if (!Array.isArray(positions)) return null;

        const transformedPositions = positions.map((rowValue, reelIndex) => {
          return [reelIndex, rowValue];
        });

        // Derive winning symbol — pick first non-wild from the screen
        let mappedSymbol = null;
        for (let p = 0; p < positions.length; p++) {
          const row = positions[p];
          const screenSymbol = screen?.[p]?.[row];
          if (screenSymbol !== undefined) {
            const mapped = PROVIDER_CONFIG.SYMBOL_MAP[screenSymbol] !== undefined
              ? PROVIDER_CONFIG.SYMBOL_MAP[screenSymbol]
              : (typeof screenSymbol === "string" ? parseInt(screenSymbol, 10) : screenSymbol);
            if (mapped !== PROVIDER_CONFIG.WILD_SYMBOL_ID) {
              mappedSymbol = mapped;
              break;
            }
          }
        }
        // Fallback: all wilds — use first symbol
        if (mappedSymbol === null && positions.length > 0) {
          const row = positions[0];
          const screenSymbol = screen?.[0]?.[row];
          if (screenSymbol !== undefined) {
            mappedSymbol = PROVIDER_CONFIG.SYMBOL_MAP[screenSymbol] !== undefined
              ? PROVIDER_CONFIG.SYMBOL_MAP[screenSymbol]
              : (typeof screenSymbol === "string" ? parseInt(screenSymbol, 10) : screenSymbol);
          }
        }

        return {
          id: lineId,
          win: amount,
          positions: transformedPositions,
          symbol: mappedSymbol,
        };
      })
      .filter((w) => w !== null);
  }

  // ── Adapt Round to State ──

  adaptSpinRoundToState(normalizedOutcome, bet) {
    if (!normalizedOutcome) return null;

    const totalWin = normalizedOutcome.win || 0;
    const winType = this.computeWinType(totalWin, bet);

    const round = {
      reels: normalizedOutcome.reels || [],
      wins: normalizedOutcome.wins || [],
      win: totalWin,
      bet,
      winType,
      isAnimatedWin: winType !== "" && PROVIDER_CONFIG.ANIMATED_WIN_TYPES.includes(winType),
      storage: normalizedOutcome.storage || null,
    };

    if (FEATURES_CONFIG.TENSION.ANTICIPATION?.ENABLED) {
      round.anticipation = FEATURES_CONFIG.TENSION.ANTICIPATION.FORCE
        ? true
        : (normalizedOutcome.anticipation || false);
    }

    return round;
  }

  // ── Response Handler ──

  getResponseHandler() {
    return {
      patterns: {
        freeSpins: [
          {
            condition: () => false,
            extractFreeSpins: () => [],
            extractTriggerRound: (round) => round,
          },
        ],
        regular: {
          condition: (response) =>
            response.flow?.state === "ready" ||
            response.flow?.state === "closed" ||
            response.flow?.state === "freespins",
          extractRound: (outcome) => {
            if (!outcome) return {};
            const transformedOutcome = { ...outcome };
            transformedOutcome.wins = Array.isArray(outcome.wins)
              ? this.normalizeWins(outcome.wins, outcome.screen)
              : [];
            return transformedOutcome;
          },
        },
      },
      propertyMap: {
        balance: "balance.wallet",
        bet: "outcome.bet",
        win: "balance.game",
        token: "flow.round_id",
        round: "outcome",
        reels: "outcome.screen",
        wins: "outcome.wins",
      },
    };
  }

  // ── Init Game ──

  async initGame(useApiData = false) {
    if (!window.__OPTIONS__) {
      throw new Error("Dmon: window.__OPTIONS__ not found.");
    }

    const apiUrl = window.__OPTIONS__?.api;
    const playToken = window.__OPTIONS__?.play_token;
    const csrfHeaderName = window.__OPTIONS__?.csrfTokenHeaderName;
    const csrfHeaderValue = window.__OPTIONS__?.csrfTokenHeaderValue;
    const identifier = window.__OPTIONS__?.identifier;

    const missingFields = [];
    if (!apiUrl) missingFields.push("api");
    if (!playToken) missingFields.push("play_token");
    if (!csrfHeaderName) missingFields.push("csrfTokenHeaderName");
    if (!csrfHeaderValue) missingFields.push("csrfTokenHeaderValue");
    if (!identifier) missingFields.push("identifier");

    if (missingFields.length > 0) {
      throw new Error(
        `Dmon: Missing required fields in window.__OPTIONS__: [${missingFields.join(", ")}]`
      );
    }

    try {
      const response = await axios.post(
        apiUrl,
        { command: "init" },
        {
          headers: {
            [csrfHeaderName]: csrfHeaderValue,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.data) {
        throw new Error("Dmon: Invalid API response - response.data is missing");
      }

      if (
        response.data.errors &&
        Array.isArray(response.data.errors) &&
        response.data.errors.length > 0
      ) {
        const error = response.data.errors[0];
        throw new Error(`Dmon: API error: ${error.desc} (code: ${error.code})`);
      }

      const availableBets = response.data?.options?.available_bets;
      const defaultBet = response.data?.options?.default_bet;
      const baseBet = response.data?.options?.base_bet;
      const paytable = response.data?.options?.paytable;
      const lines = response.data?.options?.lines;
      const wallet = response.data?.balance?.wallet;
      const currencyObj = response.data?.options?.currency;
      const featureMultipliers =
        response.data?.options?.feature_options?.feature_multipliers;

      const jackpotMultipliers =
        response.data?.options?.jackpots ||
        response.data?.options?.feature_options?.jackpots ||
        null;

      const coinValues = response.data?.options?.coin_values || null;
      const progress = response.data?.progress || null;

      // Validate required fields
      if (!availableBets || !Array.isArray(availableBets)) {
        throw new Error("Dmon: Invalid API response - options.available_bets is missing or invalid");
      }
      if (typeof defaultBet !== "number") {
        throw new Error("Dmon: Invalid API response - options.default_bet is missing or invalid");
      }
      if (typeof baseBet !== "number") {
        throw new Error("Dmon: Invalid API response - options.base_bet is missing or invalid");
      }
      if (!paytable || typeof paytable !== "object") {
        throw new Error("Dmon: Invalid API response - options.paytable is missing or invalid");
      }
      if (!lines || !Array.isArray(lines)) {
        throw new Error("Dmon: Invalid API response - options.lines is missing or invalid");
      }
      this.lines = lines;
      if (typeof wallet !== "number") {
        throw new Error("Dmon: Invalid API response - balance.wallet is missing or invalid");
      }
      if (!currencyObj || typeof currencyObj.code !== "string") {
        throw new Error("Dmon: Invalid API response - options.currency.code is missing or invalid");
      }

      // Bonus buy multipliers from feature_options
      // Formula: currentBet * feature_multiplier / 100 = buy bonus price (in subunits)
      // Divide raw API values by 100 to get the multiplier applied to currentBet.
      // Example: bonus_buy=8000 → 8000/100 = 80× multiplier
      //          EUR bet=100 (1.00€) → 100 * 80 = 8000 subunits = 80.00€
      //          mBTC bet=1000 → 1000 * 80 = 80000 subunits = 0.8000 mBTC
      // Handles sim format { bonus_buy: 2000 } and real format { bonus_buy: { 1: 8000, 2: 30000 } }
      let bonusBuyMultiplier;
      let bonusBuyExtraMultiplier;

      if (featureMultipliers?.bonus_buy) {
        if (typeof featureMultipliers.bonus_buy === "object") {
          const level1Cost = featureMultipliers.bonus_buy["1"] || featureMultipliers.bonus_buy[1];
          const level2Cost = featureMultipliers.bonus_buy["2"] || featureMultipliers.bonus_buy[2];
          bonusBuyMultiplier = level1Cost ? level1Cost / 100 : undefined;
          bonusBuyExtraMultiplier = level2Cost ? level2Cost / 100 : undefined;
        } else {
          bonusBuyMultiplier = featureMultipliers.bonus_buy / 100;
          bonusBuyExtraMultiplier = featureMultipliers.bonus_buy_extra
            ? featureMultipliers.bonus_buy_extra / 100
            : undefined;
        }
      }

      const currencyConfig = {
        subunits: currencyObj.subunits || 100,
        exponent: currencyObj.exponent !== undefined ? currencyObj.exponent : 2,
        symbol: currencyObj.symbol || currencyObj.code || "",
      };


      let featureState = this.detectFeatureState(response.data);
      const storedState = this.loadGameState(response.data);

      // ── Completion Reload: bonus finished but localStorage not yet cleared ──
      // After bonus ends, the API returns a normal base game init (no bonus data).
      // But localStorage still has the full bonus state until the user presses spin.
      // Synthesize a completion featureState so the game replays the collection.
      if (
        featureState.type !== "bonus" &&
        StorageManager.hasBonusGameRound()
      ) {
        const savedRound = StorageManager.loadBonusGameRound();
        if (savedRound) {
          featureState = {
            type: "bonus",
            phase: "completion",
            data: {
              respinGameType: savedRound.respinGameType || "",
              currentRound: savedRound.round,
              initialRespinScreen: savedRound.round?.reels || savedRound.initialRespinScreen,
              initialRespinValues: savedRound.valuesGrid || savedRound.initialRespinValues,
              initialRespinOriginalValues: savedRound.originalValues || savedRound.initialRespinOriginalValues,
              valuesGrid: savedRound.valuesGrid,
              originalValues: savedRound.originalValues,
              prevScreen: savedRound.prevScreen,
              respinsLeft: savedRound.respinsLeft,
              respinsIssued: savedRound.respinsIssued,
              coinCount: savedRound.coinCount || 0,
              totalWin: savedRound.totalWin || 0,
              triggerCoordinates: savedRound.triggerCoordinates,
              initialEvents: savedRound.initialEvents || [],
              flippedCoinPositions: savedRound.flippedCoinPositions || [],
              screenProgress: [],
              isReload: true,
            },
          };
        }
      }

      // On reload during bonus, attach round data for grid restoration
      if (
        featureState.type === "bonus" &&
        (featureState.phase === "in_progress" || featureState.phase === "intro")
      ) {
        const outcome = response.data?.outcome;
        if (outcome?.screen && Array.isArray(outcome.screen)) {
          const normalizedScreen = normalizeScreenData(outcome.screen);
          featureState.data.currentRound = {
            reels: normalizedScreen,
            wins: outcome.wins || [],
            win: response.data?.balance?.game || 0,
            storage: outcome.storage || {},
          };
          if (featureState.phase === "in_progress") {
            featureState.data.isReload = true;
            // Ensure trigger_win key exists for WinDisplay on reload.
            // If not already set (from live play), bootstrap with balance.game.
            const existingTriggerWin = StorageManager.loadTriggerWin();
            if (!existingTriggerWin) {
              StorageManager.saveTriggerWin(response.data?.balance?.game || 0);
            }
          }
        }
      }

      // ── Reconstruct base game round when localStorage is missing during bonus ──
      // On bonus init the server provides features.trigger_screen (the base game
      // screen that triggered the bonus) — use it to rebuild the base game round.
      // During intro, outcome.screen is the same; during in_progress it's the bonus grid.
      // Without this, extractRoundFromInit would use options.screen (wrong random screen).
      if (
        featureState.type === "bonus" &&
        (featureState.phase === "intro" || featureState.phase === "in_progress") &&
        !StorageManager.hasBaseGameRound()
      ) {
        const triggerScreen = response.data?.features?.trigger_screen || response.data?.outcome?.screen;
        if (triggerScreen && Array.isArray(triggerScreen)) {
          const normalizedReels = normalizeScreenData(triggerScreen);
          const triggerCoords = response.data?.features?.trigger_coordinates || [];

          // Intro: full trigger round with coin wins (needed for entry animation).
          // In-progress: stripped — first respin already cleared wins; only reels matter.
          const isIntro = featureState.phase === "intro";

          // Build synthetic coin win from trigger coordinates (Zeus symbol positions)
          const syntheticWins = isIntro && triggerCoords.length > 0
            ? [{ id: null, win: 0, positions: triggerCoords, symbol: null, isCoin: true }]
            : [];

          const outcomeBet = response.data?.outcome?.bet || defaultBet;
          const outcomeWin = response.data?.outcome?.win || 0;
          const winType = isIntro ? this.computeWinType(outcomeWin, outcomeBet) : "";

          const reconstructedRound = {
            reels: normalizedReels,
            wins: syntheticWins,
            win: outcomeWin,
            bet: outcomeBet,
            winType,
            isAnimatedWin: isIntro && winType !== "" && PROVIDER_CONFIG.ANIMATED_WIN_TYPES.includes(winType),
            triggerWin: isIntro, // Only true for intro — in_progress is already stripped
          };

          StorageManager.saveBaseGameRound({ round: reconstructedRound, bet: outcomeBet });
        }
      }

      const balance = wallet;
      const savedBaseGame = StorageManager.loadBaseGameRound();
      const bet = storedState.bet || savedBaseGame?.bet || defaultBet;
      const round = useApiData
        ? this.extractRoundFromInit(response.data)
        : storedState.round || savedBaseGame?.round || this.extractRoundFromInit(response.data);

      const gameMode = this.getGameMode(featureState);

      // ── Free Rounds detection ──
      // Free rounds arrive as top-level `free_rounds` + `balance.free_rounds`
      const freeRoundsData = response.data?.free_rounds || null;
      const freeRoundsTotalWin = response.data?.balance?.free_rounds ?? 0;

      const result = {
        balance,
        bet,
        baseBet,
        bets: availableBets,
        round,
        token: playToken,
        currency: currencyObj.code,
        currencyConfig,
        paytable: Object.fromEntries(
          Object.entries(paytable).map(([apiId, values]) => [
            PROVIDER_CONFIG.SYMBOL_MAP[apiId] ?? apiId,
            values,
          ])
        ),
        winningLines: lines,
        bonusBuyMultiplier,
        bonusBuyExtraMultiplier,
        game: identifier,
        gameMode,
        featureState,
        reloadState: storedState.reloadState,
        availableActions: response.data?.flow?.available_actions || ["init", "spin"],
        flowState: response.data?.flow?.state || "ready",
        features: response.data?.features || null,
        freeRounds: freeRoundsData,
        freeRoundsTotalWin,
        command: "init",
        jackpotMultipliers,
        coinValues,
        progress,
      };

      // Persist base game on first load
      if (
        result.gameMode === "base" &&
        result.round &&
        (!StorageManager.hasBaseGameRound() || useApiData)
      ) {
        StorageManager.saveBaseGameRound({ round: result.round, bet: result.bet, balance: result.balance });
      }

      // Persist free rounds state on init (for reload recovery)
      if (freeRoundsData?.status === "active") {
        if (StorageManager.hasFreeRoundsRound()) {
          // Exists → update changing fields, never touch betToRestore
          StorageManager.saveFreeRoundsRound({
            status: freeRoundsData.status,
            betValue: freeRoundsData.bet_value,
            performed: freeRoundsData.performed,
            count: freeRoundsData.count,
            win: freeRoundsTotalWin,
          });
        } else {
          // Doesn't exist → create with betToRestore from base_game_round
          // savedBaseGame was loaded BEFORE base_game_round gets overwritten (line 568)
          StorageManager.saveFreeRoundsRound({
            status: freeRoundsData.status,
            betValue: freeRoundsData.bet_value,
            performed: freeRoundsData.performed,
            count: freeRoundsData.count,
            win: freeRoundsTotalWin,
            betToRestore: savedBaseGame?.bet ?? result.bet,
          });
        }

        // ── FR round 0: reset base game to fresh neutral state ──
        // No rounds played yet — use fresh API reels with no wins.
        // Prevents stale base game win from showing on reload.
        if (freeRoundsData.performed === 0) {
          // Clear stale bonus game data — if FR was assigned during a bonus,
          // the bonus is finished but localStorage wasn't cleared (no base spin
          // happened between bonus end and FR activation).
          StorageManager.clearBonusGameRound();

          const freshRound = this.extractRoundFromInit(response.data);
          if (freshRound) {
            StorageManager.saveBaseGameRound({
              round: freshRound,
              bet: savedBaseGame?.bet ?? result.bet,
              balance: result.balance,
            });
            // Also update the result so the current session uses fresh reels
            result.round = freshRound;
          }
        }
      }

      // ── FR canceled externally (operator revoked during bonus or base game) ──
      // Throw 206 immediately to trigger error recovery overlay.
      // This resets the entire game state and pushes the user to a clean base game.
      if (freeRoundsData?.status === "canceled") {
        StorageManager.clearFreeRoundsRound();
        StorageManager.clearBonusGameRound();
        const cancelError = {
          errors: [{ code: 206, desc: "free_rounds_canceled" }],
        };
        throw cancelError;
      }

      // ── FR Completion Reload: FR finished but localStorage not yet cleared ──
      // After FR ends, the API returns no free_rounds (base game init).
      // But localStorage still has FR data until the user plays a base game spin.
      // Synthesize a "played" status so the slice sets completionPending
      // and the hook replays the completion popups.
      // EXCEPTION: If performed < count, the FR was canceled externally (operator),
      // not completed — clear stale localStorage instead of showing completion.
      if (
        !freeRoundsData?.status &&
        StorageManager.hasFreeRoundsRound()
      ) {
        const savedFR = StorageManager.loadFreeRoundsRound();
        if (savedFR) {
          const wasCompleted = savedFR.performed >= savedFR.count;
          if (wasCompleted) {
            // Legitimate completion — synthesize "played" for completion popups
            result.freeRounds = {
              status: "played",
              bet_value: savedFR.betValue,
              performed: savedFR.performed,
              count: savedFR.count,
              // Tells the slice whether `win` below already includes the last
              // round's own win (reveal played before reload) or not (reload
              // interrupted the reveal) — decides if withholding is valid.
              lastRoundApplied: savedFR.lastRoundApplied ?? true,
            };
            result.freeRoundsTotalWin = savedFR.win ?? 0;
          } else {
            // External cancellation — clear stale localStorage, start fresh
            StorageManager.clearFreeRoundsRound();
          }
        }
      }

      return result;
    } catch (error) {
      console.error("[Dmon RGS] initGame failed", error?.message || error);
      // Structured errors (from API or synthesized 206) — pass through as-is
      if (error?.errors && Array.isArray(error.errors)) {
        throw error;
      }
      if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
        const apiError = error.response.data.errors[0];
        throw new Error(`Dmon: API error: ${apiError.desc} (code: ${apiError.code})`);
      }
      if (error.message?.startsWith("Dmon:")) throw error;
      throw new Error(`Dmon: Init API call failed: ${error.message}`);
    }
  }

  // ── Spin ──

  spin(bet, token, command, purchasedFeature, spinOptions) {
    return new Promise((resolve, reject) => {
      if (!this.worker) {
        this.worker = createInlineWorker(spinWorkerCode);
      }

      const options = window.__OPTIONS__;
      if (!options) {
        reject(new Error("Dmon: window.__OPTIONS__ not found."));
        return;
      }

      const apiUrl = options.api;
      const csrfTokenHeaderName = options.csrfTokenHeaderName;
      const csrfTokenHeaderValue = options.csrfTokenHeaderValue;
      const identifier = options.identifier;
      const playToken = options.play_token;

      const missingFields = [];
      if (!apiUrl) missingFields.push("api");
      if (!csrfTokenHeaderName) missingFields.push("csrfTokenHeaderName");
      if (!csrfTokenHeaderValue) missingFields.push("csrfTokenHeaderValue");
      if (!identifier) missingFields.push("identifier");

      if (missingFields.length > 0) {
        reject(new Error(`Dmon: Missing required fields in window.__OPTIONS__: [${missingFields.join(", ")}]`));
        return;
      }

      this.worker.onmessage = (event) => {
        const data = event.data;

        if (data.error) {
          let errorCode = null;
          if (typeof data.error === "object" && data.error.errors && Array.isArray(data.error.errors)) {
            errorCode = data.error.errors[0]?.code;
          }

          if (errorCode && PROVIDER_CONFIG.ERROR_CODES[errorCode] !== undefined) {
            errorCode = PROVIDER_CONFIG.ERROR_CODES[errorCode];
            if (data.error.errors && data.error.errors[0]) {
              data.error.errors[0].code = errorCode;
            }
          }

          let extractedBalance = null;
          if (typeof data.error === "object" && data.error.balance) {
            if (typeof data.error.balance === "object" && data.error.balance.wallet !== undefined) {
              extractedBalance = data.error.balance.wallet;
            } else if (typeof data.error.balance === "number") {
              extractedBalance = data.error.balance;
            }
          }

          if (extractedBalance !== null && typeof data.error === "object") {
            data.error.balance = extractedBalance;
          }

          if (typeof data.error === "object" && data.error.errors) {
            reject(data.error);
          } else {
            const normalizedError = {
              errors: [{ code: errorCode || 999, desc: typeof data.error === "string" ? data.error : "Unknown error" }],
            };
            if (extractedBalance !== null) normalizedError.balance = extractedBalance;
            reject(normalizedError);
          }
          return;
        }

        // Keep raw screen before normalization (needed for bonus symbol parsing)
        const rawScreen = data.outcome?.screen
          ? data.outcome.screen.map((reel) => [...reel])
          : null;

        // Normalize symbol IDs
        if (data.outcome && Array.isArray(data.outcome.screen)) {
          const normalized = normalizeScreenData(data.outcome.screen);
          data.outcome.reels = normalized;
          data.outcome.screen = normalized;
        }
        const transformedData = data;

        if (transformedData.flow?.round_id) {
          this.currentToken = transformedData.flow.round_id;
        }

        let extractedBalance = transformedData.balance;
        if (transformedData.balance && typeof transformedData.balance === "object" && transformedData.balance.wallet !== undefined) {
          extractedBalance = transformedData.balance.wallet;
        }

        let normalizedOutcome = transformedData.outcome;
        if (transformedData.outcome) {
          const handler = this.getResponseHandler();
          normalizedOutcome = handler.patterns.regular.extractRound(transformedData.outcome);
          // Use balance.game as the win value — same as outcome.win but persists
          // during bonus respins (outcome.win resets to 0, balance.game accumulates)
          normalizedOutcome.win = transformedData.balance?.game ?? normalizedOutcome.win;
        }

        // Forward anticipation flag
        const anticipation = transformedData.features?.anticipation ?? transformedData.anticipation;
        if (anticipation !== undefined) {
          normalizedOutcome.anticipation = anticipation;
        }

        if (normalizedOutcome && transformedData.flow?.round_id) {
          normalizedOutcome.id = transformedData.flow.round_id;
        }

        const isRespin = command === "respin";
        let featureState = this.detectFeatureState(transformedData);

        // Bonus game: attach currentRound for trigger/respin
        const isBonusTrigger =
          featureState.type === "bonus" &&
          featureState.phase === "intro" &&
          command === "spin";
        const isBonusRespin =
          featureState.type === "bonus" && command === "respin";

        if (featureState.type === "bonus" && (isBonusTrigger || isBonusRespin)) {
          const originalScreen = rawScreen || data.outcome?.screen;
          if (originalScreen && Array.isArray(originalScreen)) {
            const normalizedReels = normalizeScreenData(originalScreen);
            featureState.data.currentRound = {
              reels: normalizedReels,
              wins: normalizedOutcome.wins || [],
              win: normalizedOutcome.win || 0,
              bet: normalizedOutcome.bet,
              storage: transformedData.outcome?.storage || null,
            };
          }
        }

        const gameMode = this.getGameMode(featureState);

        // Balance: trigger spin defers win (added at bonus end)
        const winAmount = normalizedOutcome?.win || 0;
        let finalBalance;
        if (isBonusTrigger) {
          finalBalance = extractedBalance;
        } else {
          finalBalance = extractedBalance + winAmount;
        }

        const adaptedRound = this.adaptSpinRoundToState(normalizedOutcome, bet);

        // On trigger: inject synthetic coin win for Zeus symbols from initial_respin_screen
        if (isBonusTrigger && adaptedRound) {
          const respinScreen = featureState.data?.initialRespinScreen;
          if (Array.isArray(respinScreen)) {
            const ZEUS_IDS = [ZEUS_DOUBLE_ID, ZEUS_MYSTERY_ID, ZEUS_COLLECT_ID];
            const zeusCoords = [];

            for (let reel = 0; reel < respinScreen.length; reel++) {
              const reelData = respinScreen[reel];
              if (!Array.isArray(reelData)) continue;
              for (let row = 0; row < reelData.length; row++) {
                if (ZEUS_IDS.includes(reelData[row])) {
                  zeusCoords.push([reel, row]);
                }
              }
            }

            if (zeusCoords.length > 0) {
              const existingWins = (adaptedRound.wins || []).filter(w => !w.isCoin);
              adaptedRound.wins = [
                ...existingWins,
                {
                  id: null,
                  win: 0,
                  positions: zeusCoords,
                  symbol: null,
                  isCoin: true,
                },
              ];
            }
          }
        }

        // triggerWin: single flag for useWinController — true when there's any win or a bonus trigger
        if (adaptedRound) {
          adaptedRound.triggerWin = (adaptedRound.win > 0) || isBonusTrigger;
        }

        const gameState = {
          balance: finalBalance,
          bet: transformedData.outcome?.bet || null,
          round: adaptedRound || normalizedOutcome,
          gameMode,
          featureState,
        };

        this.saveGameState(gameState);

        // ── Trigger spin: save triggerWin immediately ──
        // Capture the base game win that triggered the bonus BEFORE any respins.
        // This is frozen for the entire bonus — respins never overwrite it.
        if (isBonusTrigger) {
          const baseTriggerWin = adaptedRound?.win ?? 0;
          StorageManager.saveTriggerWin(baseTriggerWin);
        }

        // Save bonus game round to localStorage on each respin
        if (isBonusRespin && featureState.data?.currentRound) {
          // Safety: only strip + save if this is a confirmed valid bonus respin response.
          // Requires phase=in_progress, a non-empty reels grid, and respinsLeft defined.
          // This prevents accidental stripping on malformed/partial responses.
          const isValidBonusRespinResponse =
            featureState.phase === "in_progress" &&
            Array.isArray(featureState.data.currentRound.reels) &&
            featureState.data.currentRound.reels.length > 0 &&
            featureState.data.respinsLeft !== undefined;

          if (isValidBonusRespinResponse) {
            const isFirstRespin = !StorageManager.hasBonusGameRound();
            // On first respin, strip wins from base game round so reload won't replay win animations
            if (isFirstRespin) {
              StorageManager.stripBaseGameWins();
            }
            const respinsLeft = featureState.data?.respinsLeft ?? 0;
            StorageManager.saveBonusGameRound({
              round: featureState.data.currentRound,
              bet: gameState.bet,
              respinsLeft,
              respinsIssued: featureState.data?.respinsIssued ?? 3,
              // totalWin: only needed for completion reload — set on last respin only
              totalWin: respinsLeft === 0 ? (gameState.round?.win ?? 0) : 0,
              coinCount: featureState.data?.coinCount ?? 0,
              respinGameType: featureState.data?.respinGameType || "",
              initialRespinScreen: featureState.data?.initialRespinScreen || null,
              initialRespinValues: featureState.data?.initialRespinValues || null,
              initialRespinOriginalValues: featureState.data?.initialRespinOriginalValues || null,
              initialEvents: featureState.data?.initialEvents || [],
              triggerCoordinates: featureState.data?.triggerCoordinates || null,
              valuesGrid: featureState.data?.valuesGrid || null,
              originalValues: featureState.data?.originalValues || null,
              prevScreen: featureState.data?.prevScreen || null,
              screenProgress: featureState.data?.screenProgress || [],
              // Accumulate flippedCoinPositions: load previous + process current events
              flippedCoinPositions: this._accumulateFlippedPositions(featureState),
              // Win limit flag — BE source of truth
              winLimitReached: featureState.data?.winLimitReached || false,
            });
          }
        }
        // NOTE: stripBaseGameWins() is intentionally NOT called here on isBonusTrigger.
        // Until the first actual respin is saved, every reload must land on intro phase
        // and replay the trigger spin win animation. The strip happens on the FIRST respin
        // above (inside the isBonusRespin block, guarded by !hasBonusGameRound()).

        if (command === "spin" && gameMode === "base") {
          // Always clear bonus game round on base game spin
          StorageManager.clearBonusGameRound();
          // Only clear free rounds data when FR is finished —
          // check localStorage since API response format may vary
          const currentFR = StorageManager.loadFreeRoundsRound();
          if (!currentFR || currentFR.status !== "active") {
            StorageManager.clearFreeRoundsRound();
          }
        }

        // ── Free Rounds from spin response ──
        const freeRoundsData = transformedData.free_rounds || null;
        const freeRoundsTotalWin = transformedData.balance?.free_rounds ?? null;

        // Save FR state to localStorage on each FR spin (for reload recovery)
        if (freeRoundsData) {
          if (StorageManager.hasFreeRoundsRound()) {
            // Exists → only update changing fields, never touch betToRestore
            StorageManager.saveFreeRoundsRound({
              status: freeRoundsData.status,
              betValue: freeRoundsData.bet_value,
              performed: freeRoundsData.performed,
              count: freeRoundsData.count,
              win: freeRoundsTotalWin ?? StorageManager.loadFreeRoundsRound()?.win ?? 0,
              // This round's own win is NOT folded into `win` yet — that only
              // happens once its reveal plays (WinDisplay → updateFreeRoundsWin).
              // Set immediately, synchronously, so a reload right here already
              // knows the persisted total is pre-reveal.
              lastRoundApplied: false,
            });
          } else {
            // Doesn't exist → create with betToRestore from base_game_round
            StorageManager.saveFreeRoundsRound({
              status: freeRoundsData.status,
              betValue: freeRoundsData.bet_value,
              performed: freeRoundsData.performed,
              count: freeRoundsData.count,
              win: freeRoundsTotalWin ?? 0,
              betToRestore: StorageManager.loadBaseGameRound()?.bet ?? null,
              lastRoundApplied: false,
            });
            // Strip stale base game wins so reload at FR round 0 won't replay
            // the previous base game win animation. Bet is already captured above.
            StorageManager.stripBaseGameWins();
          }
        }

        const result = {
          token: transformedData.flow?.round_id || null,
          bet: gameState.bet,
          balance: finalBalance,
          round: adaptedRound || normalizedOutcome,
          gameMode,
          featureState,
          availableActions: transformedData.flow?.available_actions || ["init", "spin"],
          flowState: transformedData.flow?.state || "ready",
          features: transformedData.features || null,
          freeRounds: freeRoundsData,
          freeRoundsTotalWin,
          command,
          progress: transformedData.progress || null,
        };

        // Attach raw respin round for bonus grid rendering
        if (isRespin) {
          const originalScreen = rawScreen || data.outcome?.screen;
          if (originalScreen && Array.isArray(originalScreen)) {
            const respinRound = { ...normalizedOutcome };
            respinRound.reels = normalizeScreenData(originalScreen);
            result.respinRound = respinRound;
          }
        }
        // ── FR cancellation on any spin/respin ──
        // Server indicates FR was revoked. Reject with 206 immediately
        // to trigger error recovery overlay and reset the game cleanly.
        if (freeRoundsData?.status === "canceled") {
          StorageManager.clearFreeRoundsRound();
          StorageManager.clearBonusGameRound();
          reject({
            errors: [{ code: 206, desc: "free_rounds_canceled" }],
          });
          return;
        }

        resolve(result);
      };

      this.worker.onerror = (event) => {
        reject(new Error(`Dmon: Worker error: ${event.message || "Unknown worker error"}`));
      };

      const tokenToUse = token || this.currentToken || playToken;
      const purchasedFeatureLevel = spinOptions?.purchased_feature_level || null;

      this.worker.postMessage({
        bet,
        token: tokenToUse,
        apiUrl,
        csrfTokenName: csrfTokenHeaderName,
        csrfTokenValue: csrfTokenHeaderValue,
        identifier,
        command,
        ...(purchasedFeature ? { purchasedFeature } : {}),
        ...(purchasedFeatureLevel ? { purchasedFeatureLevel } : {}),
        ...(spinOptions ? { options: spinOptions } : {}),
      });
    });
  }

  // ── Spin Params ──

  getSpinParams({ bet, featureState, freeRoundsActive, freeRoundsBet, purchasedFeature, purchasedLevel }) {
    if (featureState?.type === "bonus") {
      return { command: "respin", spinOptions: undefined };
    }

    // Free rounds: regular spin command with bonus flag and FR bet (same as Dmon)
    if (freeRoundsActive) {
      return { command: "spin", spinOptions: { bonus: true, bet: freeRoundsBet || bet } };
    }

    // Bonus buy: include level so the API knows which tier
    if (purchasedFeature && purchasedLevel) {
      const spinOpts = { purchased_feature_level: String(purchasedLevel) };

      const { DEV_FEATURE_COMBO } = require("../../../devSimulation");
      if (DEV_FEATURE_COMBO) {
        spinOpts.feature_combo = DEV_FEATURE_COMBO;
      }

      return {
        command: "spin",
        spinOptions: spinOpts,
      };
    }

    return { command: "spin", spinOptions: undefined };
  }

  // ── Buy Feature ──

  // Level "1" → random features (80× bet), level "2" → all 3 DMC (300× bet)
  async buyFeature(bet, level = "1") {
    const state = (await import("../../../State/Store/store")).default.getState();
    const token = state.game.token;
    return this.spin(bet, token, "spin", "bonus_buy", {
      purchased_feature_level: String(level),
    });
  }

  async simulate(slotface, bet, token) {
    throw new Error("simulate() must be implemented");
  }

  // ── Balance Strategy ──

  getBalanceStrategy() {
    return {
      serverIncludesWin: false,
      clientDeductsBet: true,
      clientAddsWin: true,
    };
  }

  shouldSyncBalanceOnWinClose() {
    return true;
  }

  // ── Session Management ──


  getCurrentToken() {
    return this.currentToken || null;
  }

  // ── Feature Detection ──

  // Detect bonus game state from API response.
  // Discriminator: features.respin_game_type present + flow.state === "respin"
  // Phase detection:
  //   "intro"       — bonus triggered, no respins played yet (storage is null)
  //   "in_progress" — mid-bonus, respins already played (storage has data)
  //   "outro"       — completion phase (future)
  detectFeatureState(apiResponse) {
    const { flow, features } = apiResponse;

    if (
      features?.respins_left !== undefined &&
      features?.respin_game_type &&
      (flow?.state === "respin" || features.trigger_screen !== undefined)
    ) {
      // Phase detection:
      //   spin command  → always intro (trigger spin from base game)
      //   respin command → always in_progress (actively playing bonus)
      //   init command   → check outcome.storage: null = intro, present = in_progress
      let phase;
      if (flow?.command === "spin") {
        phase = "intro";
      } else if (flow?.command === "respin") {
        phase = "in_progress";
      } else if (flow?.command === "init" && flow?.state === "respin") {
        phase = apiResponse.outcome?.storage ? "in_progress" : "intro";
      } else {
        phase = "intro";
      }

      const storage = apiResponse.outcome?.storage || {};

      // Fix raw multiplier values → actual win amounts using outcome.wins
      const outcomeWins = apiResponse.outcome?.wins || [];
      if (outcomeWins.length > 0) {
        const winLookup = {};
        for (const win of outcomeWins) {
          if (win[0] === "coin" && win.length >= 4) {
            winLookup[`${win[2]}_${win[3]}`] = win[1];
          }
        }

        if (storage.values && Array.isArray(storage.values)) {
          for (let ri = 0; ri < storage.values.length; ri++) {
            if (!Array.isArray(storage.values[ri])) continue;
            for (let si = 0; si < storage.values[ri].length; si++) {
              const key = `${ri}_${si}`;
              if (winLookup[key] !== undefined) {
                storage.values[ri][si] = winLookup[key];
              }
            }
          }
        }

        if (features.initial_respin_values && Array.isArray(features.initial_respin_values)) {
          for (let ri = 0; ri < features.initial_respin_values.length; ri++) {
            if (!Array.isArray(features.initial_respin_values[ri])) continue;
            for (let si = 0; si < features.initial_respin_values[ri].length; si++) {
              const key = `${ri}_${si}`;
              if (winLookup[key] !== undefined) {
                features.initial_respin_values[ri][si] = winLookup[key];
              }
            }
          }
        }
      }

      // Detect new coins: diff prev_screen vs outcome.screen (blank → non-blank)
      let newCoins = [];
      if (phase === "in_progress") {
        const prevScreen = storage.prev_screen || normalizeScreenData(features.initial_respin_screen) || [];
        const currentScreen = apiResponse.outcome?.screen || [];
        for (let ri = 0; ri < currentScreen.length; ri++) {
          if (!Array.isArray(currentScreen[ri])) continue;
          for (let si = 0; si < currentScreen[ri].length; si++) {
            const prevId = parseInt(prevScreen?.[ri]?.[si], 10) || 0;
            const currId = parseInt(currentScreen[ri][si], 10) || 0;
            if (prevId === BLANK_SYMBOL_ID && currId !== BLANK_SYMBOL_ID) {
              let zeusFeature = null;
              if (currId === ZEUS_MYSTERY_ID || currId === CM_MINOR_ID || currId === CM_MINI_ID || currId === CM_MAJOR_ID) {
                zeusFeature = "M";
              } else if (currId === ZEUS_DOUBLE_ID || currId === CD_MULT_ID) {
                zeusFeature = "D";
              } else if (currId === ZEUS_COLLECT_ID) {
                zeusFeature = "C";
              }
              newCoins.push({
                reelIndex: ri,
                symbolIndex: si,
                symbolId: currId,
                isZeus: zeusFeature !== null,
                zeusFeature,
              });
            }
          }
        }
      }

      // Build events list (new API: events/initial_events, legacy: screen_progress)
      const rawScreenProgress = phase === "intro"
        ? [...(features.initial_events || features.initial_screen_progress || []), ...(storage.events || storage.screen_progress || [])]
        : (storage.events || storage.screen_progress || []);

      return {
        type: "bonus",
        phase,
        data: {
          respinsLeft: features.respins_left,
          respinsIssued: features.respins_issued ?? 3,
          respinGameType: features.respin_game_type,
          triggerScreen: features.trigger_screen,
          triggerCoordinates: features.trigger_coordinates || null,
          initialRespinScreen: normalizeScreenData(features.initial_respin_screen) || null,
          initialRespinValues: features.initial_respin_values || null,
          screenProgress: this._normalizeScreenProgress(rawScreenProgress),
          coinValues: storage.coin_values || null,
          prevScreen: storage.prev_screen || null,
          valuesGrid: storage.values || null,
          originalValues: storage.original_values || null,
          initialRespinOriginalValues: features.initial_respin_original_values || null,
          newCoins,
          // Coin count: use API field when available, fallback to counting non-blanks
          // initial_respin_screen has zeus coins (non-16) — use on trigger/init
          // outcome.screen is the bonus grid only on respin (on trigger/init it's the base game screen)
          coinCount: storage.coins_count
            ?? features.initial_coins_count
            ?? features.coin_count
            ?? 0,
          winLimitReached: (() => {
            if (storage.win_limit_reached) return true;
            const totalWin = apiResponse.balance?.game || 0;
            const betAmount = apiResponse.outcome?.bet || 0;
            const maxMultiplier = FEATURES_CONFIG.POT_COLLECTION?.MAX_WIN_MULTIPLIER || 10000;
            return betAmount > 0 && totalWin >= betAmount * maxMultiplier;
          })(),
          currentRound: apiResponse.outcome ? {
            reels: apiResponse.outcome.screen || [],
            wins: apiResponse.outcome.wins || [],
            win: apiResponse.balance?.game || 0,
            storage,
          } : null,
          // The activation event log for the trigger spin — used to determine which mystery
          // coins were actually revealed (m_reveals) during the initial activation sequence.
          initialEvents: features.initial_events || features.initial_screen_progress || [],
        },
      };
    }

    return { type: null, phase: null, data: null };
  }

  // Lightweight bonus detection for reload (used by collectFeatureStates)
  detectBonusFromApi(apiResponse) {
    const { flow, features, outcome } = apiResponse;
    if (
      features?.respins_left === undefined ||
      !features?.respin_game_type ||
      (flow?.state !== "respin" && features?.trigger_screen === undefined)
    ) {
      return null;
    }
    let phase;
    if (flow?.command === "spin") {
      phase = "intro";
    } else if (flow?.command === "respin") {
      phase = "in_progress";
    } else if (flow?.command === "init" && flow?.state === "respin") {
      phase = outcome?.storage ? "in_progress" : "intro";
    } else {
      phase = "intro";
    }
    const storage = outcome?.storage || {};
    return {
      phase,
      data: {
        respinsLeft: features.respins_left,
        respinsIssued: features.respins_issued ?? 3,
        respinGameType: features.respin_game_type,
        triggerScreen: features.trigger_screen,
        triggerCoordinates: features.trigger_coordinates || null,
        initialRespinScreen: normalizeScreenData(features.initial_respin_screen) || null,
        initialRespinValues: features.initial_respin_values || null,
        screenProgress: this._normalizeScreenProgress(
            phase === "intro"
              ? [
                ...(features.initial_events || features.initial_screen_progress || []),
                ...(storage.events || storage.screen_progress || []),
              ]
              : (storage.events || storage.screen_progress || [])
          ),
        coinValues: storage.coin_values || null,
        originalValues: storage.original_values || null,
        initialRespinOriginalValues: features.initial_respin_original_values || null,
        // The activation event log for the trigger spin — used to determine which mystery
        // coins were actually revealed (m_reveals) during the initial activation sequence.
        // Mystery coins NOT in this list are still "raw" and must show as jackpot coins on reload.
        initialEvents: features.initial_events || features.initial_screen_progress || [],
      },
    };
  }

  // ── Screen Progress Helpers ──

  /**
   * Accumulate flippedCoinPositions across respins.
   * Loads the previous set from localStorage, then processes the CURRENT round's
   * events (d_doubles, c_collect) to add any newly flipped positions.
   * For the first save (trigger intro), processes initial_events instead.
   */
  _accumulateFlippedPositions(featureState) {
    const BLANK_ID = 16;
    const data = featureState.data || {};

    // Load previously accumulated flipped positions
    const savedRound = StorageManager.loadBonusGameRound();
    const existing = new Set(savedRound?.flippedCoinPositions || []);

    // Determine which events to process:
    // - First save (no previous round): process initial_events (trigger) + current storage.events
    // - Subsequent saves: only current round's storage.events (prior flips are in localStorage)
    const isFirstSave = !savedRound;
    const rawStorageEvts = data.currentRound?.storage?.events || data.currentRound?.storage?.screen_progress || [];
    const rawEvents = isFirstSave
      ? [...(data.initialEvents || []), ...rawStorageEvts]
      : rawStorageEvts;

    // Normalize events so both cells[] and coordinates[] formats produce [reel, row] pairs
    const events = this._normalizeScreenProgress(rawEvents);

    // Build onBoard: prevScreen (all coins before this spin) + new coins from this round
    const onBoard = new Set();
    if (!isFirstSave) {
      // Seed from prevScreen
      const ps = data.prevScreen || data.currentRound?.storage?.prev_screen || [];
      for (let ri = 0; ri < ps.length; ri++) {
        if (!Array.isArray(ps[ri])) continue;
        for (let si = 0; si < ps[ri].length; si++) {
          const cellId = parseInt(ps[ri][si], 10);
          if (!isNaN(cellId) && cellId !== BLANK_ID) {
            onBoard.add(`${ri}_${si}`);
          }
        }
      }
    }

    // Process normalized events (each has coordinates: [reel, row])
    for (const event of events) {
      const eventType = event.type || event.action;
      const coords = event.coordinates;
      if (!Array.isArray(coords)) continue;
      const pairs = Array.isArray(coords[0]) ? coords : [coords];

      if (eventType === 'new_coins' || eventType === 'm_reveals') {
        for (const p of pairs) {
          if (Array.isArray(p) && p.length >= 2) onBoard.add(`${p[0]}_${p[1]}`);
        }
      } else if (eventType === 'd_doubles') {
        for (const p of pairs) {
          if (Array.isArray(p) && p.length >= 2) existing.add(`${p[0]}_${p[1]}`);
        }
      } else if (eventType === 'c_collect') {
        for (const key of onBoard) existing.add(key);
      }
    }

    const result = [...existing];
    return result;
  }


  // Normalize screen_progress coordinate formats to consistent flat pairs
  // and split batched coordinates into individual actions
  _normalizeScreenProgress(rawProgress) {
    if (!rawProgress || !Array.isArray(rawProgress)) return [];

    const EXPAND_ACTIONS = new Set(["new_coins", "m_reveals", "d_reveals", "c_collect"]);

    const normalized = [];
    for (const rawAction of rawProgress) {
      const actionType = rawAction.type || rawAction.action;

      // ── NEW FORMAT: cells array ──
      if (rawAction.cells && Array.isArray(rawAction.cells)) {
        if (EXPAND_ACTIONS.has(actionType)) {
          // Split each cell into an individual action (1 event per cell)
          for (const cell of rawAction.cells) {
            normalized.push({
              action: actionType,
              coordinates: [cell.reel, cell.row],
              value: cell.value ?? 0,
              ...(cell.tier ? { tier: cell.tier } : {}),
            });
          }
        } else {
          // d_doubles: keep as array of pairs (batch operation)
          normalized.push({
            action: actionType,
            coordinates: rawAction.cells.map(c => [c.reel, c.row]),
            cells: rawAction.cells, // preserve full cells for value access
          });
        }
        continue;
      }

      // ── LEGACY FORMAT: coordinates array (backward compat) ──
      const action = rawAction.type && !rawAction.action
        ? { ...rawAction, action: rawAction.type }
        : rawAction;

      const coords = action.coordinates;
      if (!Array.isArray(coords) || coords.length === 0) {
        normalized.push(action);
        continue;
      }

      if (EXPAND_ACTIONS.has(action.action) && Array.isArray(coords[0])) {
        // Array-of-pairs: [[0,3],[0,4]] → split into individual actions
        for (const coord of coords) {
          normalized.push({ ...action, coordinates: coord });
        }
      } else {
        normalized.push(action);
      }
    }
    return normalized;
  }

  // ── Game Mode ──

  getGameMode(featureState) {
    if (featureState.type && featureState.phase === "intro") return "base";
    return featureState.type ? "feature" : "base";
  }

  // ── Storage ──

  saveGameState(gameState) {
    const { gameMode, round, bet, balance } = gameState;
    if (gameMode === "base") {
      StorageManager.saveBaseGameRound({ round, bet, balance });
    }
  }

  // ── Reload ──

  collectFeatureStates(apiResponse) {
    return {
      bonus: {
        api: this.detectBonusFromApi(apiResponse),
      },
    };
  }

  determineFeaturePriority(features) {
    if (features.bonus.api) {
      return { primary: "bonus" };
    }
    return { primary: "base" };
  }

  buildBaseGameReload() {
    const savedBaseGame = StorageManager.loadBaseGameRound();
    if (savedBaseGame) {
      return { round: savedBaseGame.round, bet: savedBaseGame.bet, reloadState: { needsReload: false } };
    }
    return { reloadState: { needsReload: false } };
  }

  loadGameState(apiResponse) {
    this.collectFeatureStates(apiResponse);
    // PC reload is handled by potCollectionSlice via featureState
    return this.buildBaseGameReload();
  }

  clearCompletionData() {
    // Clear bonus game round data on first base game spin after bonus
    StorageManager.clearBonusGameRound();
    // Clear free rounds data on first base game spin after FR completion
    StorageManager.clearFreeRoundsRound();
  }

  extractRoundFromInit(apiResponse) {
    const screen = apiResponse?.options?.screen;
    if (screen && Array.isArray(screen)) {
      return { reels: normalizeScreenData(screen), wins: [], win: 0 };
    }
    return null;
  }
}

export { spinWorkerCode };

export default DmonRGS;
